/**
 * NotificationsRealtimeProvider
 *
 * Centraliza — 1 vez por sessão — a subscription Realtime das notificações
 * do agente, o toast (sonner) e a entrega multi-canal (som + push do browser).
 * Todos os consumidores compartilham o mesmo cache TanStack Query, portanto
 * navegar entre páginas não gera novas conexões nem novas chamadas RPC.
 *
 * Responsabilidades:
 *  1. Manter 1 canal Realtime `agent-notifications-realtime` por usuário.
 *  2. Ao receber INSERT: atualizar cache (`agent-notifications:list` e
 *     `agent-notifications:unread-count`), disparar toast e delivery.
 *  3. Ao receber UPDATE (ex.: marcada como lida em outra aba): reconciliar
 *     apenas a contagem via `invalidateQueries` (leve).
 *  4. Escutar mensagens `FCM_NAVIGATE` do Service Worker.
 *  5. Ao reconectar após queda, invalidar o cache uma única vez para
 *     recuperar eventos perdidos.
 *
 * @version 1.0.0 - 2026-07-02
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNotificationDelivery } from '@/hooks/useNotificationDelivery';
import {
  agentNotificationsListKey,
  agentNotificationsUnreadCountKey,
  mapRawToAgentNotification,
  type AgentNotification,
} from '@/hooks/useAgentNotifications';
import { createLogger } from '@/lib/logger';

const logger = createLogger('NotificationsRealtimeProvider');

/** Mesmo limite default do hook — mantém a chave de cache alinhada. */
const DEFAULT_LIMIT = 20;

/**
 * Constrói o toast a partir do tipo — mesma lógica que existia antes no hook.
 * Extraído aqui para o provider ser a única fonte de toasts.
 */
function buildToastFor(
  notification: AgentNotification,
  helpers: {
    navigate: (url: string) => void;
    navigateToConversation: (n: AgentNotification) => void;
  },
) {
  const contactName = notification.metadata?.contact_name || 'Contato';

  let icon = '📩';
  let description = notification.message || `Nova conversa de ${contactName}`;

  if (
    notification.type === 'transfer_received' ||
    notification.type === 'department_transfer'
  ) {
    icon = '🔄';
  } else if (notification.type === 'activity_mention') {
    icon = '@';
    description = notification.message || 'Clique para ver o comentário';
    // v1.2.0: menção em mensagem privada (nota interna) do chat
  } else if (notification.type === ('chat_note_mention' as AgentNotification['type'])) {
    icon = '@';
    description = notification.message || 'Você foi mencionado em uma mensagem privada';
  } else if (notification.type === 'scheduling_booking') {
    icon = '📅';
    description = notification.message || 'Novo agendamento recebido';
  } else if (notification.type === 'contract_signed') {
    icon = '✍️';
    description = notification.message || 'Contrato assinado';
  } else if (notification.type === 'payment_received') {
    icon = '💰';
    description = notification.message || 'Pagamento recebido';
  } else if (notification.type === 'task_assigned') {
    icon = '📋';
    description =
      notification.message || 'Você foi definido como responsável de uma tarefa';
  } else if (notification.type === 'task_comment_added') {
    icon = '💬';
    description = notification.message || 'Novo andamento na tarefa';
  } else if (notification.type === 'task_comment_mention') {
    icon = '@';
    description = notification.message || 'Você foi mencionado em um andamento';
  } else if (notification.type === 'deal_seller_assigned') {
    icon = '💼';
    description = notification.metadata?.is_reassignment
      ? 'Um negócio foi transferido para você'
      : 'Você foi definido como vendedor de um negócio';
  }

  let action: { label: string; onClick: () => void } | undefined;
  if (notification.type === 'activity_mention') {
    action = {
      label: 'Ver atividade',
      onClick: () => helpers.navigate('/tenant/crm/activities'),
    };
  } else if (notification.conversation_id) {
    action = {
      label: 'Ver conversa',
      onClick: () => helpers.navigateToConversation(notification),
    };
  }

  toast(notification.title, {
    description,
    duration: 8000,
    action,
    icon,
  });
}

interface Props {
  children: React.ReactNode;
}

/**
 * Provider que ativa a subscription Realtime + toasts + delivery UMA vez.
 * Deve ser montado dentro de `AuthProvider` e `QueryClientProvider`.
 */
export function NotificationsRealtimeProvider({ children }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Delivery multi-canal (som + browser push). Fica em ref para não recriar
  // callbacks a cada mudança nas preferências do usuário.
  const { deliver } = useNotificationDelivery();
  const deliverRef = useRef(deliver);
  useEffect(() => {
    deliverRef.current = deliver;
  }, [deliver]);

  // Evita processar o mesmo id 2x (caso o INSERT chegue via Realtime e
  // também pela lista inicial — improvável, mas defensivo).
  const processedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.id) return;

    const listKey = agentNotificationsListKey(user.id, DEFAULT_LIMIT);
    const countKey = agentNotificationsUnreadCountKey(user.id);

    const handleInsert = (payload: { new: Record<string, unknown> }) => {
      const notification = mapRawToAgentNotification(payload.new);
      if (processedIds.current.has(notification.id)) return;
      processedIds.current.add(notification.id);

      // Atualiza cache da lista (prepend, limitado ao tamanho da janela).
      queryClient.setQueryData<AgentNotification[]>(listKey, (prev) => {
        if (!prev) return [notification];
        if (prev.some((n) => n.id === notification.id)) return prev;
        return [notification, ...prev.slice(0, DEFAULT_LIMIT - 1)];
      });

      // Incremento otimista do contador; será reconciliado logo em seguida.
      if (!notification.is_read) {
        queryClient.setQueryData<number>(countKey, (prev) => (prev ?? 0) + 1);
      }

      // Toast + entrega multi-canal.
      try {
        buildToastFor(notification, {
          navigate,
          navigateToConversation: (n) => {
            if (n.conversation_id) {
              navigate(`/tenant/chat?conversation=${n.conversation_id}`);
            }
          },
        });
      } catch (err) {
        logger.warn('Falha ao exibir toast:', err);
      }
      try {
        deliverRef.current?.(notification);
      } catch (err) {
        logger.warn('Falha na entrega multi-canal:', err);
      }

      // Reconcilia contagem oficial com o servidor (cobre divergências).
      queryClient.invalidateQueries({ queryKey: countKey });
    };

    const channel = supabase
      .channel(`agent-notifications-realtime-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'agent_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        handleInsert,
      )
      // Cobre marcações "as read" feitas em outra aba/dispositivo.
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'agent_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: countKey });
        },
      )
      .subscribe((status) => {
        logger.debug('Status do canal Realtime:', status);
        // Ao (re)conectar, invalida list+count para recuperar eventos perdidos
        // durante o período em que o canal estava CLOSED/CHANNEL_ERROR.
        if (status === 'SUBSCRIBED') {
          queryClient.invalidateQueries({ queryKey: listKey });
          queryClient.invalidateQueries({ queryKey: countKey });
        }
      });

    return () => {
      supabase.removeChannel(channel);
      processedIds.current.clear();
    };
  }, [user?.id, queryClient, navigate]);

  // Escuta cliques em popups nativos do Service Worker (FCM) — antes vivia
  // duplicado em cada consumidor de `useAgentNotifications`.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handleSwMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (!data || data.type !== 'FCM_NAVIGATE' || !data.url) return;
      try {
        window.dispatchEvent(
          new CustomEvent('notification:open', { detail: { url: data.url } }),
        );
        navigate(data.url);
      } catch (err) {
        logger.warn('Falha ao navegar a partir do popup do SW:', err);
      }
    };
    navigator.serviceWorker.addEventListener('message', handleSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSwMessage);
    };
  }, [navigate]);

  return <>{children}</>;
}
